import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper, Stack } from '@mui/material';
import Typewriter from 'typewriter-effect';
import { gsap } from 'gsap';

const Hero: React.FC = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLDivElement | null>(null);
  const subRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      gsap.from([headingRef.current, subRef.current], {
        opacity: 0,
        y: 20,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.1,
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  return (
    <Paper
      ref={rootRef}
      sx={{
        position: 'relative',
        color: 'text.primary',
        mt: 0,
        mb: 1,
        borderRadius: 2,
        overflow: 'hidden',
        p: { xs: 3, md: 4 },
        background:
          'radial-gradient(800px 400px at 0% 0%, rgba(34,211,238,0.08), transparent), radial-gradient(900px 500px at 100% 0%, rgba(167,139,250,0.08), transparent)',
        border: '1px solid rgba(34,211,238,0.15)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
        backdropFilter: 'blur(8px)',
        '&::before': {
          content: '"// Cloud Ambassadors Evaluation Portal"',
          position: 'absolute',
          top: 14,
          left: 20,
          fontSize: '10px',
          fontFamily: '"Fira Code", "JetBrains Mono", monospace',
          color: 'rgba(103,232,249,0.35)',
          letterSpacing: '0.5px',
        },
        '&::after': {
          content: '"█"',
          position: 'absolute',
          top: 14,
          right: 20,
          fontSize: '10px',
          color: 'rgba(16,185,129,0.5)',
          animation: 'blink 1s infinite',
        },
        '@keyframes blink': {
          '0%, 49%': { opacity: 1 },
          '50%, 100%': { opacity: 0 },
        },
      }}
      elevation={0}
    >
      {/* Gradient blobs */}
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        '&::before': {
          content: '""', position: 'absolute', width: 350, height: 350, borderRadius: '50%',
          filter: 'blur(100px)', opacity: 0.3, left: -100, top: -100,
          background: 'linear-gradient(135deg, rgba(34,211,238,0.4), rgba(6,182,212,0.2))'
        },
        '&::after': {
          content: '""', position: 'absolute', width: 380, height: 380, borderRadius: '50%',
          filter: 'blur(100px)', opacity: 0.3, right: -120, top: -80,
          background: 'linear-gradient(135deg, rgba(167,139,250,0.4), rgba(124,58,237,0.2))'
        }
      }} />

      <Stack spacing={3} sx={{ position: 'relative', maxWidth: 980, mt: 3 }}>
        <Box ref={headingRef}>
          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: -1,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              background: 'linear-gradient(90deg, #e6f1ff 0%, #67e8f9 30%, #c4b5fd 70%, #e6f1ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            CA-Evaluate
          </Typography>
          <Box sx={{
            fontSize: { xs: 22, md: 28 },
            color: 'text.secondary',
            mt: 1,
            fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace',
          }}>
            <Typewriter
              options={{
                strings: [
                  '> Evaluating_the_next_gen_cloud_ambassadors...',
                  '> Technical_excellence_meets_opportunity',
                  '> Building_the_future_of_cloud_computing',
                ],
                autoStart: true,
                loop: true,
                delay: 40,
                deleteSpeed: 25,
              }}
            />
          </Box>
        </Box>

        <Typography
          ref={subRef}
          variant="h6"
          color="text.secondary"
          sx={{
            maxWidth: 820,
            fontWeight: 400,
            lineHeight: 1.6,
            '& code': {
              fontFamily: '"Fira Code", "JetBrains Mono", monospace',
              background: 'rgba(34,211,238,0.1)',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '0.9em',
              color: '#67e8f9',
              border: '1px solid rgba(34,211,238,0.2)',
            }
          }}
        >
          Your gateway to the <code>Cloud Ambassadors</code> organisation — where technical prowess meets innovation. Complete your assessments for a chance to join the club.
        </Typography>
      </Stack>
    </Paper>
  );
};

export default Hero;

